import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  MicrophoneStage, Stop, Play, Pause, Trash, PaperPlaneTilt, UploadSimple, ArrowClockwise,
} from '@phosphor-icons/react';
import { chunkedUpload } from '../utils/chunkedUpload';

const MAX_SECONDS = 300; // 5 min for exercise replies
const MAX_BYTES = 20 * 1024 * 1024; // 20 MB

const fmt = (s) => {
  const mm = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

function pickMime() {
  if (typeof MediaRecorder === 'undefined') return '';
  const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  for (const c of cands) if (MediaRecorder.isTypeSupported?.(c)) return c;
  return '';
}

/**
 * AudioRecorder: record from mic (pause/resume), preview (play/pause with
 * seekable controls + duration), and upload on Send. Also supports picking an
 * existing audio file.
 *
 * Props:
 *  - onUploaded(file): called with { url, name, type, size, duration } on upload success
 *  - maxSeconds (default 300)
 *  - testId prefix for data-testid
 *  - compact (bool): smaller layout
 */
export default function AudioRecorder({
  onUploaded,
  maxSeconds = MAX_SECONDS,
  testId = 'audio',
  compact = false,
  hidden = false,
}) {
  const [state, setState] = useState('idle'); // idle | recording | paused | preview | uploading
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [duration, setDuration] = useState(0);

  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const filenameRef = useRef(null);

  // Revoke previous blob URL when replaced
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const cleanupMediaStream = () => {
    try { streamRef.current?.getTracks?.().forEach(t => t.stop()); } catch {}
    streamRef.current = null;
  };

  const stopTimer = () => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } };
  const startTimer = () => {
    stopTimer();
    tickRef.current = setInterval(() => {
      setSeconds((s) => {
        const n = s + 1;
        if (n >= maxSeconds) stopRecording();
        return n;
      });
    }, 1000);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mimeType = pickMime();
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mrRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const type = mr.mimeType || 'audio/webm';
        const b = new Blob(chunksRef.current, { type });
        const url = URL.createObjectURL(b);
        setBlob(b);
        setBlobUrl(url);
        setState('preview');
        stopTimer();
        cleanupMediaStream();
        filenameRef.current = `grabacion-${Date.now()}.${type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm'}`;
      };
      mr.start();
      setSeconds(0);
      setState('recording');
      startTimer();
    } catch (e) {
      toast.error('Micrófono no disponible o permiso denegado');
      console.error(e);
    }
  };

  const pauseRecording = () => {
    if (mrRef.current && mrRef.current.state === 'recording') {
      mrRef.current.pause();
      setState('paused');
      stopTimer();
    }
  };

  const resumeRecording = () => {
    if (mrRef.current && mrRef.current.state === 'paused') {
      mrRef.current.resume();
      setState('recording');
      startTimer();
    }
  };

  const stopRecording = useCallback(() => {
    try { if (mrRef.current && mrRef.current.state !== 'inactive') mrRef.current.stop(); }
    catch {}
  }, []);

  const discard = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null); setBlobUrl(null); setSeconds(0); setDuration(0); setState('idle');
  };

  const handlePickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > MAX_BYTES) { toast.error(`Audio > ${Math.round(MAX_BYTES / 1024 / 1024)} MB`); return; }
    const url = URL.createObjectURL(f);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(f); setBlobUrl(url); setState('preview');
    filenameRef.current = f.name;
  };

  const send = async () => {
    if (!blob) return;
    if (blob.size > MAX_BYTES) { toast.error(`Audio > ${Math.round(MAX_BYTES / 1024 / 1024)} MB`); return; }
    setState('uploading');
    try {
      const file = new File([blob], filenameRef.current || `audio-${Date.now()}.webm`, { type: blob.type || 'audio/webm' });
      const res = await chunkedUpload(file, 'content');
      await onUploaded?.({
        url: res.storage_path, name: file.name, type: file.type, size: file.size, duration,
      });
      discard();
    } catch (err) {
      toast.error('Error al enviar audio');
      console.error(err);
      setState('preview');
    }
  };

  // Live-audio preview ref for knowing metadata duration
  const audioPreviewRef = useRef(null);

  // When the host component opts out of audio (e.g. Grammatik dudas), render
  // nothing. We declare this AFTER all hooks have run to respect Rules of Hooks.
  if (hidden) return null;

  return (
    <div className={`rounded-xl border border-stone-200 bg-white ${compact ? 'p-2' : 'p-3'} space-y-2`} data-testid={testId}>
      {state === 'idle' && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={startRecording}
            className="inline-flex items-center gap-2 bg-[#AB1C24] text-white px-3 py-2 rounded-full text-sm font-semibold hover:bg-[#8A151C]"
            data-testid={`${testId}-record`}
          >
            <MicrophoneStage size={16} weight="fill" /> Grabar
          </button>
          <label className="inline-flex items-center gap-2 bg-white border border-stone-300 hover:bg-stone-50 px-3 py-2 rounded-full text-sm font-semibold cursor-pointer">
            <UploadSimple size={16} /> Subir archivo
            <input type="file" accept="audio/*" className="hidden" onChange={handlePickFile} data-testid={`${testId}-pick`} />
          </label>
          <span className="text-xs text-[#78716C]">Máx. {Math.floor(maxSeconds / 60)} min · {Math.round(MAX_BYTES / 1024 / 1024)} MB</span>
        </div>
      )}

      {(state === 'recording' || state === 'paused') && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-full ${state === 'recording' ? 'bg-red-50 text-red-700 animate-pulse' : 'bg-amber-50 text-amber-700'}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${state === 'recording' ? 'bg-red-500' : 'bg-amber-500'}`} />
            <span className="font-mono font-bold text-sm">{fmt(seconds)} / {fmt(maxSeconds)}</span>
          </div>
          {state === 'recording' ? (
            <button onClick={pauseRecording} className="inline-flex items-center gap-1 bg-amber-500 text-white px-3 py-2 rounded-full text-sm font-semibold hover:bg-amber-600" data-testid={`${testId}-pause`}>
              <Pause size={14} weight="fill" /> Pausa
            </button>
          ) : (
            <button onClick={resumeRecording} className="inline-flex items-center gap-1 bg-emerald-500 text-white px-3 py-2 rounded-full text-sm font-semibold hover:bg-emerald-600" data-testid={`${testId}-resume`}>
              <Play size={14} weight="fill" /> Reanudar
            </button>
          )}
          <button onClick={stopRecording} className="inline-flex items-center gap-1 bg-[#292524] text-white px-3 py-2 rounded-full text-sm font-semibold hover:bg-black" data-testid={`${testId}-stop`}>
            <Stop size={14} weight="fill" /> Parar
          </button>
        </div>
      )}

      {state === 'preview' && blobUrl && (
        <div className="space-y-2">
          <audio
            ref={audioPreviewRef}
            src={blobUrl}
            controls
            onLoadedMetadata={(e) => {
              // Some browsers report Infinity; force probe by seeking.
              const d = e.target.duration;
              if (!isFinite(d)) {
                e.target.currentTime = 1e101;
                e.target.ontimeupdate = () => {
                  e.target.ontimeupdate = null;
                  e.target.currentTime = 0;
                  setDuration(e.target.duration || seconds);
                };
              } else setDuration(d);
            }}
            className="w-full h-10"
            data-testid={`${testId}-preview-audio`}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[#78716C]">
              {duration > 0 ? fmt(duration) : fmt(seconds)} · {(blob.size / 1024).toFixed(0)} KB
            </span>
            <button onClick={send}
              className="inline-flex items-center gap-1 bg-[#AB1C24] text-white px-3 py-2 rounded-full text-sm font-semibold hover:bg-[#8A151C] ml-auto"
              data-testid={`${testId}-send`}
            >
              <PaperPlaneTilt size={14} weight="fill" /> Enviar
            </button>
            <button onClick={discard}
              className="inline-flex items-center gap-1 bg-stone-100 hover:bg-stone-200 px-3 py-2 rounded-full text-sm font-semibold text-[#57534E]"
              data-testid={`${testId}-discard`}
            >
              <Trash size={14} /> Descartar
            </button>
            <button onClick={startRecording}
              className="inline-flex items-center gap-1 bg-white border border-stone-300 hover:bg-stone-50 px-3 py-2 rounded-full text-sm font-semibold"
              data-testid={`${testId}-retry`}
            >
              <ArrowClockwise size={14} /> Regrabar
            </button>
          </div>
        </div>
      )}

      {state === 'uploading' && (
        <div className="flex items-center gap-2 text-sm text-[#78716C]">
          <span className="w-3 h-3 rounded-full border-2 border-[#AB1C24] border-t-transparent animate-spin" />
          Enviando…
        </div>
      )}
    </div>
  );
}
